import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import {
  berechtigungen as holeBerechtigungen,
  bestaetigungBeantragen,
  einzeln as holeBenutzer,
  fernAbmelden,
  geraetWiderrufen,
  geraete as holeGeraete,
  notfallcodes as neueNotfallcodes,
  passwortZuruecksetzen,
  rolleEntziehen,
  rolleZuweisen,
  rollen as holeRollen,
  sitzungen as holeSitzungen,
  statusSetzen,
  zweitenFaktorZuruecksetzen,
  type Berechtigung,
  type BenutzerZeile,
  type Rolle,
} from '../api/verwaltung';
import type { Geraet, Sitzung } from '../api/anmeldung';
import { ApiFehler } from '../api/fehler';
import {
  Aktenrumpf,
  Kennzahlenleiste,
  Reiterleiste,
  Verlauf,
  type Kennzahl,
  type Verlaufseintrag,
} from '../komponenten/Akte';
import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Meldung } from '../komponenten/Meldung';
import type { Kopf } from '../komponenten/Rahmen';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Skelett } from '../start/Widget';

type Reiter = 'rollen' | 'zugang' | 'sitzungen';

/*
 * CRM-104: die Handlung am Zugang, nach der das Pop-up gerade fragt.
 * Entsperren, Zuweisen und der Antrag auf die Administratorrolle fragen nicht
 * nach: Sie nehmen niemandem etwas, und der Gegenknopf nimmt sie ohne Verlust
 * zurück — der Antrag wirkt ohnehin erst mit der zweiten Person (BEN-09).
 */
type Nachfrage =
  | { readonly art: 'sperren' }
  | { readonly art: 'entziehen'; readonly rolle: Rolle }
  | { readonly art: 'kennwort' }
  | { readonly art: 'faktor' }
  | { readonly art: 'notfallcodes' }
  | { readonly art: 'sitzung'; readonly sitzung: Sitzung }
  | { readonly art: 'alleSitzungen' }
  | { readonly art: 'geraet'; readonly geraet: Geraet };

/** Was mit den offenen Sitzungen geschieht, in einem Satz. */
function sitzungssatz(anzahl: number): string {
  if (anzahl === 0) return 'Eine offene Sitzung hat das Konto nicht.';
  if (anzahl === 1) return 'Die offene Sitzung endet sofort.';
  return `Alle ${String(anzahl)} offenen Sitzungen enden sofort.`;
}

const zeit = (wert: string | null): string =>
  wert === null ? '—' : new Date(wert).toLocaleString('de-DE');

/** Ein Konto ohne Kennwort kommt nicht herein (BEN-21); ein technisches braucht keins. */
const ohneKennwortVon = (benutzer: BenutzerZeile): boolean =>
  benutzer.kontoart !== 'technisch' && !benutzer.kennwortGesetzt;

/*
 * DIE KENNZAHLENLEISTE (ÄA-41). Was man beim Öffnen wissen will, bevor man
 * einen Reiter wählt: Kommt er herein, womit, und wann zuletzt? Ein Konto
 * ohne Kennwort ist die Frist dieser Akte (BEN-21) — sie steht in Rost.
 */
function kennzahlenVon(benutzer: BenutzerZeile): readonly Kennzahl[] {
  const ohneKennwort = ohneKennwortVon(benutzer);
  return [
    {
      beschriftung: 'Status',
      wert: (
        <span className={benutzer.status === 'aktiv' ? 'marke' : 'marke marke--inaktiv'}>
          {benutzer.status}
        </span>
      ),
    },
    { beschriftung: 'Kontoart', wert: KONTOART[benutzer.kontoart] },
    {
      beschriftung: 'Rollen',
      wert: benutzer.rollenBezeichnungen.length === 0 ? 'keine' : benutzer.rollenBezeichnungen.join(', '),
    },
    {
      beschriftung: 'Kennwort',
      /* BEN-11: Ein technisches Konto meldet sich nicht an der Oberfläche an. */
      wert: benutzer.kontoart === 'technisch' ? 'entfällt' : ohneKennwort ? 'keines' : 'vergeben',
      frist: ohneKennwort,
    },
    {
      beschriftung: 'Zweiter Faktor',
      wert: benutzer.kontoart === 'technisch'
        ? 'entfällt'
        : benutzer.zweiterFaktorEingerichtet ? 'eingerichtet' : 'nicht eingerichtet',
    },
    { beschriftung: 'Letzte Anmeldung', wert: zeit(benutzer.letzteAnmeldung) },
  ];
}

const KONTOART: Readonly<Record<BenutzerZeile['kontoart'], string>> = {
  person: 'Person',
  technisch: 'technisch (Schnittstelle)',
  trainer: 'Onlinezugang Trainer',
  /* BP-01 (0225): der befristete Zugang der Betriebspruefung. */
  pruefer: 'Prüferkonto (Betriebsprüfung)',
};

/** Das Datum rechts im Verlauf, kurz wie im Entwurf („28.08."). */
function kurz(wert: string): string {
  return new Date(wert).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * Die Vertretungen, aus denen dieses Konto gerade Rechte hat (BEN-10, BEN-17).
 *
 * Die Herkunft eines Rechts heißt `vertretung:<Name>` — genau die Angabe, mit
 * der auch „Mein Konto" und der Rahmen arbeiten. Eine eigene Abfrage der
 * Vertretungen hat die Oberfläche nicht; siehe den offenen Punkt in ÄA-41.
 */
function vertretungenAus(rechte: readonly Berechtigung[]): ReadonlyMap<string, number> {
  const zahl = new Map<string, number>();
  for (const r of rechte) {
    if (!r.ueberVertretung) continue;
    for (const herkunft of r.ausRollen) {
      if (!herkunft.startsWith('vertretung:')) continue;
      const name = herkunft.slice('vertretung:'.length);
      zahl.set(name, (zahl.get(name) ?? 0) + 1);
    }
  }
  return zahl;
}

/** Stufe 1 der Benutzerakte (Auftrag CRM-165): eine Karte mit Zeilen. */
function Kontoskelett(): JSX.Element {
  return (
    <section className="karte">
      <Skelett art="liste" />
    </section>
  );
}

/**
 * Die Benutzerakte (BEN-01 bis BEN-13, BEN-17, SYS-05).
 *
 * Drei Reiter, und die Aufteilung folgt der Frage, die jemand stellt:
 *
 *   ROLLEN UND RECHTE — „was darf der?" samt der Herkunft (BEN-17). Eine
 *     Rechteliste ohne Herkunft beantwortet die Anschlussfrage nicht: „warum
 *     darf er das, und wo nehme ich es weg?"
 *   ZUGANG — „er kommt nicht herein." Kennwort, zweiter Faktor, Notfallcodes.
 *   SITZUNGEN UND GERÄTE — „sein Telefon ist weg." Beides braucht es dann,
 *     und beides wirkt verschieden.
 *
 * SEITENTYP AKTE (ÄA-41): links die Karte mit den Reitern, rechts die
 * Seitenleiste mit dem Zeitlichen — Anmeldungen und Vertretungen. Der
 * Aktenkopf mit Kennzahlenleiste steht im Seitenkopf (`beiKopf`); nur eine
 * Einbindung ohne Rahmen zeigt ihn oben in der Karte.
 */
export function Benutzerakte({
  id,
  darfPflegen,
  beiListe,
  beiKopf,
}: {
  readonly id: string;
  readonly darfPflegen: boolean;
  readonly beiListe: () => void;
  /** Meldet den Aktenkopf an den Seitenkopf des Rahmens (ÄA-41). */
  readonly beiKopf?: (kopf: Kopf) => void;
}): JSX.Element {
  const [benutzer, setzeBenutzer] = useState<BenutzerZeile | undefined>(undefined);
  const [rollen, setzeRollen] = useState<readonly Rolle[]>([]);
  const [rechte, setzeRechte] = useState<readonly Berechtigung[]>([]);
  const [sitzungen, setzeSitzungen] = useState<readonly Sitzung[]>([]);
  const [geraete, setzeGeraete] = useState<readonly Geraet[]>([]);
  const [reiter, setzeReiter] = useState<Reiter>('rollen');
  const [geheimnis, setzeGeheimnis] = useState<
    { titel: string; werte: readonly string[] } | undefined
  >(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | undefined>(undefined);

  const holen = useCallback(
    (signal?: AbortSignal) =>
      Promise.all([
        holeBenutzer(id, signal),
        holeRollen(signal),
        holeBerechtigungen(id, signal),
        holeSitzungen(id, signal),
        holeGeraete(id, signal),
      ]),
    [id],
  );

  const uebernehmen = useCallback(
    ([b, r, be, s, g]: Awaited<ReturnType<typeof holen>>): void => {
      setzeBenutzer(b);
      setzeRollen(r);
      setzeRechte(be);
      setzeSitzungen(s);
      setzeGeraete(g);
    },
    [],
  );

  /* Nach einer Handlung: neu holen. */
  const laden = useCallback(async (): Promise<void> => {
    uebernehmen(await holen());
  }, [holen, uebernehmen]);

  /* Auftrag CRM-165: drei Stufen statt „wird geladen"; ein anderes Konto laedt neu und bricht das alte ab. */
  const geladen = useLadestufe((signal) => holen(signal), id);
  const ladefehler = geladen.fehler instanceof ApiFehler ? geladen.fehler : undefined;
  useEffect(() => {
    if (geladen.daten !== undefined) uebernehmen(geladen.daten);
  }, [geladen.daten, uebernehmen]);

  async function handle(was: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await was();
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  /*
   * Aus der Rückfrage heraus (CRM-104): Scheitert die Handlung, wirft
   * `bestaetigt` — der Fehler steht im Pop-up, das offen bleibt, und nicht noch
   * einmal auf der Seite. Gelingt sie, schließt das Pop-up, und erst danach
   * wird neu geladen: Ein Fehler beim Laden gehört auf die Seite, denn die
   * Handlung selbst ist geschehen.
   */
  async function bestaetigt(tat: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await tat();
      setzeFehler(undefined);
      setzeNachfrage(undefined);
    } finally {
      setzeLaeuft(false);
    }
    await laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }

  async function entsperren(): Promise<void> {
    await handle(async () => {
      await statusSetzen(id, 'aktiv');
      setzeRueckmeldung('Das Konto ist wieder aktiv.');
      await laden();
    });
  }

  /* Die Knöpfe im Seitenkopf rufen immer die Fassung dieses Bildes — ohne dass
     der Kopf bei jedem Bild neu gemeldet wird (kein Kreislauf). */
  const kopfwege = useRef({ entsperren, beiListe });
  kopfwege.current = { entsperren, beiListe };

  /*
   * DER AKTENKOPF IM SEITENKOPF (ÄA-41), wie bei Kunde, Artikel und Beleg:
   * Name, Anmeldename, E-Mail, die Kennzahlenleiste und Sperren/Entsperren.
   * Ohne `beiKopf` (eine Einbindung ohne Rahmen) steht er oben in der Karte.
   */
  useEffect(() => {
    if (beiKopf === undefined || benutzer === undefined) return;
    beiKopf({
      kicker: `Benutzer · ${benutzer.anmeldename}`,
      titel: benutzer.name,
      unterzeile: benutzer.email,
      sinnbild: 'user',
      kennzeichen: undefined,
      kennzahlen: <Kennzahlenleiste zahlen={kennzahlenVon(benutzer)} />,
      handlungen: (
        <>
          {darfPflegen && benutzer.status === 'aktiv' && (
            <button
              type="button"
              className="knopf"
              disabled={laeuft}
              onClick={() => setzeNachfrage({ art: 'sperren' })}
            >
              Sperren …
            </button>
          )}
          {darfPflegen && benutzer.status === 'gesperrt' && (
            <button
              type="button"
              className="knopf"
              disabled={laeuft}
              onClick={() => void kopfwege.current.entsperren()}
            >
              Entsperren
            </button>
          )}
          <button type="button" className="knopf" onClick={() => kopfwege.current.beiListe()}>
            Zur Liste
          </button>
        </>
      ),
    });
  }, [beiKopf, benutzer, darfPflegen, laeuft]);

  if (benutzer === undefined) {
    const anzeigefehler = fehler ?? ladefehler;
    return (
      <>
        {anzeigefehler !== undefined && <Meldung fehler={anzeigefehler} />}
        {/* Bis die Antwort uebernommen ist, steht weiter das Skelett. */}
        <Ladeanzeige zustand={geladen} was="Das Konto" skelett={<Kontoskelett />}>
          <Kontoskelett />
        </Ladeanzeige>
      </>
    );
  }

  const hat = (kennung: string): boolean => benutzer.rollen.includes(kennung);

  const ohneKennwort = ohneKennwortVon(benutzer);

  /* ── Die Seitenleiste: Anmeldungen und Vertretungen ─────────────────── */

  const anmeldungen: readonly Verlaufseintrag[] = [
    ...(ohneKennwort
      ? [
          {
            kennung: 'ohne-kennwort',
            art: 'achtung' as const,
            sinnbild: 'warning-circle' as const,
            titel: 'Kein Kennwort vergeben',
            // BEN-21: Der Weg hinein ist „Kennwort zurücksetzen" im Reiter Zugang.
            detail: 'Hinein kommt er erst nach „Kennwort zurücksetzen“.',
            datum: '',
            dringend: true,
            beiWahl: () => setzeReiter('zugang'),
          },
        ]
      : []),
    ...sitzungen.map((s) => ({
      kennung: `sitzung-${s.id}`,
      art: 'vorgang' as const,
      sinnbild: 'door-open' as const,
      titel: s.geraet ?? 'Unbekanntes Gerät',
      detail: `offene Sitzung${s.ip === null ? '' : ` von ${s.ip}`}`,
      datum: kurz(s.letzteAktivitaet),
      beiWahl: () => setzeReiter('sitzungen'),
    })),
    ...(benutzer.letzteAnmeldung === null
      ? []
      : [
          {
            kennung: 'letzte-anmeldung',
            art: 'erledigt' as const,
            sinnbild: 'clock' as const,
            titel: 'Zuletzt angemeldet',
            detail: zeit(benutzer.letzteAnmeldung),
            datum: kurz(benutzer.letzteAnmeldung),
          },
        ]),
  ];

  const vertretungen: readonly Verlaufseintrag[] = [...vertretungenAus(rechte)].map(
    ([name, anzahl]) => ({
      kennung: `vertretung-${name}`,
      art: 'still' as const,
      sinnbild: 'user-switch' as const,
      titel: `vertritt ${name}`,
      detail: `${String(anzahl)} ${anzahl === 1 ? 'Recht' : 'Rechte'} aus der Vertretung`,
      datum: '',
      beiWahl: () => setzeReiter('rollen'),
    }),
  );

  /* ── Die Rückfrage am Zugang (CRM-104) ──────────────────────────────── */

  /** Frage, Tat und Folge je Handlung — und was „Bestätigen" dann tut. */
  const zurNachfrage = (n: Nachfrage): {
    readonly frage: string;
    readonly unterzeile: string;
    readonly tat: string;
    readonly folge: ReactNode;
    readonly tun: () => Promise<void>;
  } => {
    const wer = benutzer.name;
    switch (n.art) {
      case 'sperren':
        // BEN-21: gesperrt wird in der Administration; die Sitzungen beendet der Trigger aus 0012.
        return {
          frage: 'Benutzer sperren?',
          unterzeile: `${wer} · ${benutzer.anmeldename}`,
          tat: 'Sperren',
          folge: (
            <p>
              {wer} kann sich nicht mehr anmelden. {sitzungssatz(sitzungen.length)} „Entsperren“
              gibt den Zugang zurück.
            </p>
          ),
          tun: async () => {
            await statusSetzen(id, 'gesperrt');
            setzeRueckmeldung('Das Konto ist gesperrt. Offene Sitzungen enden dabei.');
          },
        };
      case 'entziehen':
        // BEN-03; BEN-09: an der Administratorrolle verbraucht der Entzug die Bestätigung.
        return {
          frage: 'Rolle entziehen?',
          unterzeile: `${n.rolle.bezeichnung} · ${wer}`,
          tat: 'Entziehen',
          folge: (
            <p>
              Die Rechte aus „{n.rolle.bezeichnung}“ entfallen für {wer} sofort, soweit keine
              andere Rolle sie gibt.
              {n.rolle.kennung === 'administration'
                ? ' Die Bestätigung der zweiten Person wird dabei verbraucht.'
                : ' „Zuweisen“ gibt sie zurück.'}
            </p>
          ),
          tun: async () => {
            await rolleEntziehen(id, n.rolle.kennung);
            setzeRueckmeldung(`${n.rolle.bezeichnung} entzogen.`);
          },
        };
      case 'kennwort':
        // BEN-21; BEN-06: Das Anfangskennwort muss beim ersten Anmelden gewechselt werden.
        return {
          frage: 'Kennwort zurücksetzen?',
          unterzeile: `${wer} · ${benutzer.anmeldename}`,
          tat: 'Zurücksetzen',
          folge: (
            <p>
              {ohneKennwort
                ? `${wer} hat noch kein Kennwort.`
                : `Das bisherige Kennwort gilt nicht mehr. ${sitzungssatz(sitzungen.length)}`}{' '}
              Das Anfangskennwort steht einmal hier; beim ersten Anmelden verlangt das System
              ein neues.
            </p>
          ),
          tun: async () => {
            const k = await passwortZuruecksetzen(id);
            setzeGeheimnis({ titel: `Anfangskennwort für ${wer}`, werte: [k.anfangskennwort] });
            setzeRueckmeldung('Alle Sitzungen wurden beendet.');
          },
        };
      case 'faktor':
        // SYS-05
        return {
          frage: 'Zweiten Faktor zurücksetzen?',
          unterzeile: `${wer} · ${benutzer.anmeldename}`,
          tat: 'Zurücksetzen',
          folge: (
            <p>
              {benutzer.zweiterFaktorEingerichtet
                ? 'Der eingerichtete zweite Faktor wird gelöscht.'
                : 'Ein zweiter Faktor ist nicht eingerichtet.'}{' '}
              {sitzungssatz(sitzungen.length)} Beim nächsten Anmelden richtet {wer} ihn mit dem
              QR-Code neu ein.
            </p>
          ),
          tun: async () => {
            await zweitenFaktorZuruecksetzen(id);
            setzeRueckmeldung(
              'Der zweite Faktor ist gelöscht. Beim nächsten Anmelden richtet ihn '
              + 'der Benutzer neu ein — mit dem QR-Code.',
            );
          },
        };
      case 'notfallcodes':
        // AA-003: Ein Satz Codes gilt; die alten verfallen.
        return {
          frage: 'Notfallcodes neu ausstellen?',
          unterzeile: `${wer} · ${benutzer.anmeldename}`,
          tat: 'Ausstellen',
          folge: (
            <p>
              Die bisherigen Notfallcodes von {wer} gelten sofort nicht mehr. Die neuen stehen
              einmal hier und sind danach nicht mehr abrufbar.
            </p>
          ),
          tun: async () => {
            const c = await neueNotfallcodes(id);
            setzeGeheimnis({
              titel: `Notfallcodes für ${wer} — die alten gelten nicht mehr`,
              werte: c.codes,
            });
          },
        };
      case 'sitzung':
        // BEN-07: Fernabmeldung durch die Administration.
        return {
          frage: 'Sitzung beenden?',
          unterzeile: `${n.sitzung.geraet ?? 'Unbekanntes Gerät'} · ${wer}`,
          tat: 'Beenden',
          folge: (
            <p>
              Die Sitzung „{n.sitzung.geraet ?? 'Unbekanntes Gerät'}“
              {n.sitzung.ip === null ? '' : ` (${n.sitzung.ip})`} endet sofort; {wer} muss sich
              dort neu anmelden. Ein gemerktes Gerät bleibt gemerkt.
            </p>
          ),
          tun: async () => {
            await fernAbmelden(id, n.sitzung.id);
            setzeRueckmeldung('Die Sitzung wurde beendet.');
          },
        };
      case 'alleSitzungen':
        // BEN-07
        return {
          frage: 'Alle Sitzungen beenden?',
          unterzeile: `${wer} · ${benutzer.anmeldename}`,
          tat: 'Alle beenden',
          folge: (
            <p>
              {sitzungssatz(sitzungen.length)} {wer} muss sich überall neu anmelden; gemerkte
              Geräte bleiben gemerkt.
            </p>
          ),
          tun: async () => {
            const e = await fernAbmelden(id);
            setzeRueckmeldung(`${String(e.beendet)} Sitzung(en) beendet.`);
          },
        };
      case 'geraet':
        // SYS-05: Ein Widerruf wirft niemanden hinaus.
        return {
          frage: 'Gerät widerrufen?',
          unterzeile: `${n.geraet.bezeichnung} · ${wer}`,
          tat: 'Widerrufen',
          folge: (
            <p>
              Auf „{n.geraet.bezeichnung}“ verlangt die Anmeldung wieder den Bestätigungscode. Eine
              offene Sitzung dort läuft weiter — sie endet nur mit „Beenden“.
            </p>
          ),
          tun: async () => {
            await geraetWiderrufen(id, n.geraet.id);
            setzeRueckmeldung(
              `${n.geraet.bezeichnung} wurde widerrufen. Dort wird wieder der `
              + 'Bestätigungscode verlangt.',
            );
          },
        };
    }
  };

  const offen = nachfrage === undefined ? undefined : zurNachfrage(nachfrage);

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {/*
        Ein Geheimnis — Anfangskennwort oder Notfallcodes — steht EINMAL da
        und nirgends sonst. Es bleibt stehen, bis es jemand wegklickt: Wer es
        gerade abschreibt, soll es nicht nach fünf Sekunden verlieren.
      */}
      {geheimnis !== undefined && (
        <div className="hinweiskarte">
          <p>
            <strong>{geheimnis.titel}</strong>
          </p>
          <ul className="geheimnisliste">
            {geheimnis.werte.map((w) => (
              <li key={w}>
                <code className="geheimnis__wert">{w}</code>
              </li>
            ))}
          </ul>
          <p className="feld__hilfe">
            Steht <strong>nur hier</strong> — gespeichert wird allein der Streuwert. Geben Sie
            es weiter und schließen Sie den Hinweis; danach ist es nicht mehr abrufbar.
          </p>
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => setzeGeheimnis(undefined)}
          >
            Habe ich notiert
          </button>
        </div>
      )}

      <Aktenrumpf
        haupt={
          <>
            {/* ── Der Aktenkopf in der Karte — nur ohne Seitenkopf (ÄA-41) ── */}
            {beiKopf === undefined && (
              <div className="karte">
                <div className="karte__titel">
                  <h2>{benutzer.name}</h2>
                  <small>
                    {benutzer.anmeldename} · {benutzer.email}
                  </small>
                </div>

                <Kennzahlenleiste zahlen={kennzahlenVon(benutzer)} />

                <div className="knopfreihe">
                  {darfPflegen && benutzer.status === 'aktiv' && (
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      disabled={laeuft}
                      onClick={() => setzeNachfrage({ art: 'sperren' })}
                    >
                      Sperren …
                    </button>
                  )}
                  {darfPflegen && benutzer.status === 'gesperrt' && (
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      disabled={laeuft}
                      onClick={() => void entsperren()}
                    >
                      Entsperren
                    </button>
                  )}
                  <button type="button" className="knopf knopf--klein" onClick={beiListe}>
                    Zur Liste
                  </button>
                </div>
              </div>
            )}

            <Reiterleiste
              beschriftung="Konto"
              gewaehlt={reiter}
              beiWahl={setzeReiter}
              reiter={[
                { kennung: 'rollen', beschriftung: 'Rollen und Rechte', zahl: benutzer.rollen.length },
                { kennung: 'zugang', beschriftung: 'Zugang' },
                { kennung: 'sitzungen', beschriftung: 'Sitzungen und Geräte', zahl: sitzungen.length },
              ]}
            />

            <div role="tabpanel" id={`tafel-${reiter}`} aria-labelledby={`reiter-${reiter}`}>
              {/* ── Rollen und Rechte ─────────────────────────────────────────── */}
              {reiter === 'rollen' && (
                <>
                  <div className="karte">
                    <div className="karte__titel">
                      <h2>Rollen</h2>
                      {/* BEN-03 */}
                    </div>

                    <div className="tabellenhuelle tabellenhuelle--telefon">
                      <table className="liste__tabelle">
                        <thead>
                          <tr>
                            <th scope="col">Rolle</th>
                            <th scope="col">Zugewiesen</th>
                            <th scope="col">Handlung</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rollen.map((r) => (
                            <tr key={r.kennung}>
                              {/* Auftrag CRM-79: die Kennung nur als Hinweis beim Überfahren. */}
                              <td title={`Kennung: ${r.kennung}`}>{r.bezeichnung}</td>
                              <td>
                                {hat(r.kennung) ? (
                                  <span className="marke">ja</span>
                                ) : (
                                  <span className="kanalliste__name">—</span>
                                )}
                              </td>
                              <td>
                                {darfPflegen && (
                                  <button
                                    type="button"
                                    className="knopf knopf--klein"
                                    disabled={laeuft}
                                    onClick={() => {
                                      // CRM-104: Entziehen nimmt Rechte weg und fragt nach; Zuweisen nicht.
                                      if (hat(r.kennung)) {
                                        setzeNachfrage({ art: 'entziehen', rolle: r });
                                        return;
                                      }
                                      void handle(async () => {
                                        await rolleZuweisen(id, r.kennung);
                                        setzeRueckmeldung(`${r.bezeichnung} zugewiesen.`);
                                        await laden();
                                      });
                                    }}
                                  >
                                    {hat(r.kennung) ? 'Entziehen …' : 'Zuweisen'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* BEN-09 */}
                    <p className="feld__hilfe">
                      Die Rolle <strong>Administration</strong> lässt sich nur mit der
                      Bestätigung einer zweiten Person ändern — der Versuch legt keinen Vorgang an,
                      sondern meldet, was fehlt. Beantragt wird er unter „Vier-Augen-Vorgänge".
                    </p>

                    {darfPflegen && (
                      <div className="knopfreihe">
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laeuft}
                          onClick={() =>
                            void handle(async () => {
                              await bestaetigungBeantragen({
                                handlung: hat('administration') ? 'entziehen' : 'zuweisen',
                                betroffener: id,
                                grund: `Administratorrolle für ${benutzer.name}`,
                              });
                              setzeRueckmeldung(
                                'Vorgang beantragt. Eine zweite Person muss ihn bestätigen, '
                                + 'dann lässt sich die Rolle setzen.',
                              );
                            })
                          }
                        >
                          {hat('administration') ? 'Entzug' : 'Zuweisung'} der Administratorrolle beantragen
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="karte">
                    <div className="karte__titel">
                      <h2>Berechtigungen</h2>
                      {/* BEN-17 */}
                    </div>

                    <div className="tabellenhuelle tabellenhuelle--telefon">
                      <table className="liste__tabelle">
                        <caption>{rechte.length} wirksame Rechte</caption>
                        <thead>
                          <tr>
                            <th scope="col">Objekt</th>
                            <th scope="col">Aktion</th>
                            <th scope="col">Umfang</th>
                            <th scope="col">Woher</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rechte.map((r) => (
                            <tr key={`${r.objekt}/${r.aktion}`}>
                              <td>{r.objekt}</td>
                              <td>{r.aktion}</td>
                              <td>{r.umfang}</td>
                              <td>
                                {/*
                                  BEN-17 verlangt die HERKUNFT. Ohne sie beantwortet die
                                  Liste nur „was darf er" und nicht „wo nehme ich es
                                  weg" — und das ist die Frage, die gestellt wird.
                                */}
                                {r.herkunft.join(', ')}
                                {r.ueberVertretung && (
                                  <span className="marke marke--inaktiv"> über Vertretung</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* ── Zugang ────────────────────────────────────────────────────── */}
              {reiter === 'zugang' && (
                <div className="karte">
                  <div className="karte__titel">
                    <h2>Zugang</h2>
                    {/* BEN-06, SYS-05, AA-003 */}
                  </div>

                  <div className="tabellenhuelle tabellenhuelle--telefon">
                    <table className="liste__tabelle">
                      <tbody>
                        {/*
                          DAS KENNWORT STEHT ZUERST, WEIL ES DIE ERSTE HÜRDE IST.
  
                          Ein Konto ohne Kennwort ist von außen nicht von einem mit
                          ungenutztem Kennwort zu unterscheiden — „Letzte Anmeldung" ist in
                          beiden Fällen leer. Seit dem 12.09.2026 ist dieser Zustand der
                          Normalfall bei zweiundvierzig Trainerkonten, und wer die Akte
                          öffnet, soll ihn ohne Umweg sehen.
                        */}
                        <tr>
                          <th scope="row">Kennwort</th>
                          <td>
                            {/* BEN-11 */}
                            {benutzer.kontoart === 'technisch' ? (
                              <span className="kanalliste__name">entfällt</span>
                            ) : benutzer.kennwortGesetzt ? (
                              <span className="marke">gesetzt</span>
                            ) : (
                              <span className="marke marke--warn">
                                keines — Anmeldung noch nicht möglich
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Zweiter Faktor</th>
                          <td>
                            {benutzer.zweiterFaktorEingerichtet ? (
                              <span className="marke">eingerichtet</span>
                            ) : (
                              <span className="marke marke--inaktiv">nicht eingerichtet</span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Letzte Anmeldung</th>
                          <td className="liste__einzeilig">{zeit(benutzer.letzteAnmeldung)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {darfPflegen && (
                    <div className="knopfreihe">
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        disabled={laeuft}
                        onClick={() => setzeNachfrage({ art: 'kennwort' })}
                      >
                        Kennwort zurücksetzen …
                      </button>

                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        disabled={laeuft}
                        onClick={() => setzeNachfrage({ art: 'faktor' })}
                      >
                        Zweiten Faktor zurücksetzen …
                      </button>

                      {/* Kein Eingriff: Wie „Belegpositionen neu erzeugen" ersetzt es Vorhandenes. */}
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        disabled={laeuft}
                        onClick={() => setzeNachfrage({ art: 'notfallcodes' })}
                      >
                        Notfallcodes ausstellen …
                      </button>
                    </div>
                  )}

                  {/* AA-003 */}
                  <p className="feld__hilfe">
                    Notfallcodes sind für alle ohne Diensttelefon und für das verlorene. Neue
                    auszustellen macht die alten ungültig — ein Satz Codes gilt, nicht mehrere.
                  </p>
                </div>
              )}

              {/* ── Sitzungen und Geräte ──────────────────────────────────────── */}
              {reiter === 'sitzungen' && (
                <>
                  <div className="karte">
                    <div className="karte__titel">
                      <h2>Offene Sitzungen</h2>
                      {/* BEN-07 */}
                    </div>

                    <div className="tabellenhuelle tabellenhuelle--telefon">
                      <table className="liste__tabelle">
                        <caption>{sitzungen.length} offene Sitzung(en)</caption>
                        <thead>
                          <tr>
                            <th scope="col">Gerät</th>
                            <th scope="col">Von</th>
                            <th scope="col">Zuletzt aktiv</th>
                            <th scope="col">Handlung</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sitzungen.map((s) => (
                            <tr key={s.id}>
                              <td>{s.geraet ?? 'Unbekannt'}</td>
                              <td>{s.ip ?? '—'}</td>
                              <td className="liste__einzeilig">{zeit(s.letzteAktivitaet)}</td>
                              <td>
                                {darfPflegen && (
                                  <button
                                    type="button"
                                    className="knopf knopf--klein"
                                    disabled={laeuft}
                                    onClick={() => setzeNachfrage({ art: 'sitzung', sitzung: s })}
                                  >
                                    Beenden …
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {darfPflegen && sitzungen.length > 0 && (
                      <div className="knopfreihe">
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laeuft}
                          onClick={() => setzeNachfrage({ art: 'alleSitzungen' })}
                        >
                          Alle beenden …
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="karte">
                    <div className="karte__titel">
                      <h2>Gemerkte Geräte</h2>
                      {/* SYS-05 */}
                    </div>

                    {geraete.length === 0 ? (
                      <p className="hinweiskarte">Dieser Benutzer hat kein Gerät gemerkt.</p>
                    ) : (
                      <div className="tabellenhuelle tabellenhuelle--telefon">
                        <table className="liste__tabelle">
                          <thead>
                            <tr>
                              <th scope="col">Gerät</th>
                              <th scope="col">Gemerkt am</th>
                              <th scope="col">Zustand</th>
                              <th scope="col">Handlung</th>
                            </tr>
                          </thead>
                          <tbody>
                            {geraete.map((g) => (
                              <tr key={g.id}>
                                <td>{g.bezeichnung}</td>
                                <td className="liste__einzeilig">{zeit(g.angelegtAm)}</td>
                                <td>
                                  {g.widerrufenAm !== null ? (
                                    <>
                                      <span className="marke marke--inaktiv">widerrufen</span>
                                      <span className="kanalliste__name">
                                        {' '}
                                        · {zeit(g.widerrufenAm)}
                                        {g.widerrufenVonName === null ? '' : ` durch ${g.widerrufenVonName}`}
                                      </span>
                                    </>
                                  ) : g.gueltig ? (
                                    <span className="marke">gültig</span>
                                  ) : (
                                    <span className="marke marke--inaktiv">abgelaufen</span>
                                  )}
                                </td>
                                <td>
                                  {darfPflegen && g.gueltig && (
                                    <button
                                      type="button"
                                      className="knopf knopf--eingriff knopf--klein"
                                      disabled={laeuft}
                                      onClick={() => setzeNachfrage({ art: 'geraet', geraet: g })}
                                    >
                                      Widerrufen …
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <p className="feld__hilfe">
                      Gezielt und nicht alle auf einmal: Wer ein verlorenes Telefon abschaltet, soll
                      den Rechner im Büro nicht mitabschalten. Ein Widerruf wirft niemanden hinaus —
                      er verlangt dort nur wieder den Bestätigungscode. Bei einem verlorenen Gerät
                      gehört <strong>beides</strong> dazu: Sitzung beenden und Gerät widerrufen.
                    </p>
                  </div>
                </>
              )}
            </div>
          </>
        }
        seite={
          <>
            <div className="akte__seitentitel">
              <span>Anmeldung und Vertretung</span>
            </div>
            {/* BEN-07, SYS-05: was an diesem Konto gerade offen ist. */}
            <Verlauf
              titel="Anmeldungen"
              zusatz={`${String(sitzungen.length)} offen`}
              eintraege={anmeldungen}
              leer="Noch nie angemeldet, keine offene Sitzung."
            />
            {/* BEN-10, BEN-17 */}
            <Verlauf
              titel="Vertretungen"
              eintraege={vertretungen}
              leer="Er vertritt gerade niemanden."
            />
          </>
        }
      />

      {offen !== undefined && (
        <Rueckfrage
          frage={offen.frage}
          unterzeile={offen.unterzeile}
          tat={offen.tat}
          beiBestaetigen={() => bestaetigt(offen.tun)}
          beiSchliessen={() => setzeNachfrage(undefined)}
        >
          {offen.folge}
        </Rueckfrage>
      )}
    </>
  );
}
